import {
  Component,
  ElementRef,
  ViewChild,
  Input,
  Renderer2,
  Injector,
  RendererFactory2,
  OnChanges,
  OnInit,
  OnDestroy,
  ViewEncapsulation,
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
  debounceTime,
  filter,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import { merge, Observable, of, Subject } from 'rxjs';

import { PathElement, NodeEntry } from '@alfresco/js-api';
import { ContentApiService } from '@alfresco/aca-shared';
import { AppStore, UploadFilesAction } from '@alfresco/aca-shared/store';

import {
  FileUploadCompleteEvent,
  FileUploadDeleteEvent,
  TreeBaseNode,
} from '@alfresco/adf-content-services';
import { WkTreeViewService } from '../../../../services/wk-tree-view.service';
import { InsertMediaService } from '../../../../services/insert-media.service';
import {
  currentSiteSelector,
  getPaginationData,
  SetPaginationChange,
} from '../../../../store';
import { SiteConfigService } from '../../../../services/site-config.service';
import { cloneNode } from '../../../../utils/node.utils';
import { WKFileUploadService } from '../../../../services/file-upload.service';
import { ContentService } from '../../../shared/services/content.service';

@Component({
  selector: 'wk-upload-image',
  templateUrl: './wk-upload-image.component.html',
  styleUrls: ['./wk-upload-image.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class WkUploadImageComponent implements OnChanges, OnInit, OnDestroy {
  @Input()
  mediaType: string;
  searchField = '';
  node: any;
  breadCrumbNode: any;
  data: any;
  currentSite = '';
  siteObject = [];
  parentElement: PathElement[];
  isLoading = false;
  notConfiguredFlag = false;
  isNavigate = false;
  @ViewChild('input') input: ElementRef;
  onDestroy$ = new Subject<void>();
  onSearch$ = new Subject<void>();
  searchFiles = false;
  nodeCollapsed = false;
  treeNode: TreeBaseNode[];
  nodeId: string;
  filteredObject: any;
  spinner = false;
  resultFiles = [];
  selectedMediaNodes = [];
  currentNodeId: string;
  configuredNode: any;
  @ViewChild('btn') btn: ElementRef<HTMLInputElement>;
  private readonly treeViewService: WkTreeViewService;
  private readonly insertMediaService: InsertMediaService;
  protected readonly wkUploadService: WKFileUploadService;
  rendererFactory: RendererFactory2;
  disableAddbutton = false;
  private previousNode = Object.create(null);
  private lastSelectedAudioVideo;
  public searchCompleted = false;
  public itemsPerPage: number[];
  public totalItems = 0;
  public itemsOnPage: number;
  public currentPage = 1;
  public getTotalPages = 0;
  public paging = {
    skipCount: 0,
    maxItems: 10,
  };

  private readonly paginationData$: Observable<any> =
    this.store.select<any>(getPaginationData);

  constructor(
    private readonly customContentApi: ContentApiService,
    private readonly contentService: ContentService,
    private readonly siteConfig: SiteConfigService,
    private readonly store: Store<AppStore>,
    private readonly renderer: Renderer2,
    private readonly injector: Injector
  ) {
    this.insertMediaService =
      this.injector.get<InsertMediaService>(InsertMediaService);
    this.rendererFactory =
      this.injector.get<RendererFactory2>(RendererFactory2);
    this.treeViewService =
      this.injector.get<WkTreeViewService>(WkTreeViewService);
    this.wkUploadService =
      injector.get<WKFileUploadService>(WKFileUploadService);
  }

  ngOnInit() {
    this.currentSite = localStorage.getItem('currentSite');
    const currSiteObj = this.siteConfig.getCurrentSiteObject(this.currentSite);
    this.itemsOnPage = currSiteObj?.pagination?.size || 10;
    this.itemsPerPage = currSiteObj?.pagination?.supportedPageSizes || [
      10, 25, 50,
    ];

    this.paginationData$.pipe(takeUntil(this.onDestroy$)).subscribe((data) => {
      this.resultFiles = data?.list?.entries;
      this.spinner = false;

      this.totalItems = data?.list?.pagination?.totalItems;
      this.getTotalPages = Math.ceil(this.totalItems / this.paging.maxItems);
      this.searchCompleted = true;
    });

    merge(
      this.wkUploadService.fileUploadComplete,
      this.wkUploadService.fileUploadDeleted
    )
      .pipe(
        debounceTime(300),
        filter(() => !this.wkUploadService.isUploading()),
        takeUntil(this.onDestroy$)
      )
      .subscribe(this.reloadCustomTreeViewComponent.bind(this));
    this.insertMediaService
      .getClickedFolderDetails()
      .pipe(
        switchMap((result: any) => {
          if (result) {
            return this.contentService.getNode(result.entry.id);
          }
          return of(undefined);
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe((result2) => {
        if (result2) {
          const node = result2;
          const breadNode = cloneNode(node);
          this.breadCrumbNode = breadNode.entry;
        }
      });
  }

  onPreviewClicked(event) {
    event.stopPropagation();
    this.insertMediaService.openPreviewDialog(event);
  }

  updateNode(node: NodeEntry) {
    this.node = node.entry;
    this.breadCrumbNode = cloneNode(node).entry;
  }

  ngOnChanges() {
    this.currentSite = localStorage.getItem('currentSite');
    const currSiteObj = this.siteConfig.getCurrentSiteObject(this.currentSite);

    for (const property in currSiteObj.media) {
      if (property === this.mediaType) {
        this.filteredObject = currSiteObj.media[property];
      }
    }

    this.isLoading = true;

    if (this.filteredObject?.length !== 0) {
      if (this.filteredObject.fts_path !== '') {
        const folderPath = this.filteredObject.fts_path;

        this.contentService
          .getMediaFolder(folderPath)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((data) => {
            if (data.list.entries.length) {
              this.updateNode(data.list.entries[0]);
              this.configuredNode = data.list.entries[0];
            }
            this.isLoading = false;
          });
      } else {
        this.getRootNode();
      }
    } else {
      this.isLoading = false;
      this.notConfiguredFlag = true;
    }
  }

  blurFunc() {
    const searchField = document.getElementById('searchField');
    searchField.focus();
    if (this.searchField !== '') {
      this.renderer.setStyle(this.input.nativeElement, 'display', 'block');
    } else {
      this.renderer.setStyle(this.input.nativeElement, 'display', 'none');
    }
  }

  addMedia() {
    if (this.mediaType) {
      const supportedTypes = `${
        this.filteredObject.supportedObject
          ? `,${this.filteredObject.supportedObject.join(',')}`
          : ''
      }`;
      const imgObj = {
        accept: `${this.mediaType}/*${supportedTypes}`,
        type: this.mediaType,
        mediaType: `${this.mediaType}${supportedTypes}`,
      };
      this.store.dispatch(new UploadFilesAction(imgObj));
    }
  }

  public getMedia(id: string): string {
    return this.customContentApi.getContentUrl(id);
  }

  public getPoster(tenantId: string): string {
    return this.contentService.getPosterUrl(tenantId);
  }

  async getRootNode() {
    const node: any = await this.insertMediaService.getNodeElements();
    this.node = node;
    if (node.path) {
      const breadNode = { entry: node };
      this.breadCrumbNode = cloneNode(breadNode).entry;
    }
    this.isLoading = false;
  }

  mouseDown() {
    if (this.searchField !== '') {
      this.renderer.setStyle(this.input.nativeElement, 'display', 'block');
    } else {
      this.renderer.setStyle(this.input.nativeElement, 'display', 'none');
    }
  }

  search() {
    this.insertMediaService.onPause();
    if (this.searchField) {
      this.insertMediaService.selectedNode.clear();
      this.store
        .select(currentSiteSelector)
        .pipe(
          filter((currentSite) => currentSite && currentSite !== 'no-site'),
          take(1),
          switchMap((currentSite) =>
            this.treeViewService.getSpecificFiles(
              this.mediaType,
              this.searchField,
              currentSite
            )
          ),
          takeUntil(this.onSearch$)
        )
        .subscribe((data) => {
          this.resultFiles = data?.list?.entries;
          this.spinner = false;
        });
      this.selectedMediaNodes = [];
      this.searchFiles = true;
      this.spinner = true;
    } else {
      if (this.configuredNode?.entry) {
        this.updateNode(this.configuredNode);
      } else {
        this.getRootNode();
      }
      this.searchFiles = false;
    }
  }

  onMediaClicked(event, node) {
    const selectedChild = 'selected-child';
    let cloneData = [];
    const hasClass = event.target
      .closest('.child')
      .classList?.contains(selectedChild);
    if (this.mediaType === 'image') {
      this.selectedMediaNodes = this.treeViewService.getSelectedImageNodes(
        event,
        node,
        this.renderer
      );
    } else {
      if (this.lastSelectedAudioVideo) {
        this.renderer.removeClass(
          this.lastSelectedAudioVideo.target.closest('.child'),
          selectedChild
        );
      }
      if (!hasClass) {
        this.renderer.addClass(event.target.closest('.child'), selectedChild);
        this.selectedMediaNodes = [node];
      } else {
        this.selectedMediaNodes = [];
      }
      this.lastSelectedAudioVideo = event;
    }
    cloneData = JSON.parse(JSON.stringify(this.selectedMediaNodes));
    for (const mediaData of cloneData) {
      this.insertMediaService.selectedNode.set(mediaData.id, mediaData);
    }
  }

  onPlay(event, node) {
    event.stopPropagation();
    node['isPlay'] = true;
    this.insertMediaService.onPlay(this.getMedia(node.entry.id));
    if (this.previousNode?.entry?.id === node.entry.id) {
      this.previousNode = node;
    } else {
      if (this.previousNode) {
        this.previousNode['isPlay'] = false;
      }
      this.previousNode = node;
    }
  }

  onPause(event, node) {
    event.stopPropagation();
    this.insertMediaService.onPause();
    node['isPlay'] = false;
    this.previousNode = Object.create(null);
  }

  async onBreadcrumbNavigate(route: PathElement) {
    const node = await this.insertMediaService.getBreadCrumb(route);
    this.updateNode(node);
  }

  ngOnDestroy() {
    this.store.dispatch(new UploadFilesAction(null));
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.insertMediaService.onPause();
    this.insertMediaService.setClickedFolderDetails('');
  }

  reloadCustomTreeViewComponent(
    event: FileUploadCompleteEvent | FileUploadDeleteEvent
  ): void {
    this.isLoading = true;
    const minimalNode = event.file.data as NodeEntry;
    this.contentService
      .getNode(minimalNode.entry.parentId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((node: NodeEntry) => {
        this.node = node;
        this.updateNode(node);
        this.isLoading = false;
      });
  }

  public onSelect(evt: any): void {
    this.itemsOnPage = evt.detail;
    this.currentPage = 1;

    this.paging = {
      maxItems: this.itemsOnPage,
      skipCount: 0,
    };

    this.dispatchPaginationChange();
  }

  public onPageClick(event: any): void {
    this.currentPage = event.detail;

    this.paging = {
      maxItems: this.itemsOnPage,
      skipCount: (this.currentPage - 1) * this.itemsOnPage,
    };

    this.dispatchPaginationChange();
  }

  public onSearch(): void {
    this.currentPage = 1;
    this.itemsOnPage = this.itemsOnPage || 10;
    this.paging = {
      maxItems: this.itemsOnPage,
      skipCount: 0,
    };

    this.dispatchPaginationChange();
  }

  private dispatchPaginationChange(): void {
    this.searchCompleted = false;
    this.selectedMediaNodes = [];
    this.searchFiles = true;
    this.spinner = true;
    this.store.dispatch(
      new SetPaginationChange({
        mediaType: this.mediaType,
        searchField: this.searchField,
        currentSite: this.currentSite,
        paging: this.paging,
      })
    );
  }
}
